import React, { useEffect, useReducer, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import DateFnsUtils from '@date-io/date-fns';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  KeyboardDatePicker,
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import deLocale from 'date-fns/locale/es';

import { ReactComponent as CalendarIcon } from '../../../../../assets/img/calendar.svg';
import { ReactComponent as LocationIcon } from '../../../../../assets/img/location.svg';
import { ReactComponent as TimeIcon } from '../../../../../assets/img/reloj.svg';
import WhatsappSrc from '../../../../../assets/img/whatsapp.svg';
// import WhatsappSrc from '../../../../../assets/img/whatsapp.png';
import FloatingButton from '../../../../../components/FloatingButton';
import { NumberFormatCustom } from '../../../../../components/NumberFormartCustom/NumberFormatCustom.component';
import SharedDialog from '../../../../../components/SharedDialog/SharedDialog.component';
import { appConfig } from '../../../../../config/app';
import { CustomEvents } from '../../../../../constants/analytics.constant';
import { IncidentsLabels } from '../../../../../constants/incidents.constant';
import { useAuthDataContext } from '../../../../../context/auth/auth.provider';
import { useSinisterDataContext } from '../../../../../context/sinister/sinister.provider';
import sinisterReducer, {
  CLEAN_INJURED_PEOPLE,
} from '../../../../../context/sinister/sinister.reducer';
import { CustomDataLayerPush } from '../../../../../helpers/CustomDataLayerPush';
import { dataLayerPush } from '../../../../../helpers/dataLayerPush';
import useForm from '../../../../../helpers/useForm';
import useLogEventInAnalytics from '../../../../../hooks/useLogAnalyticsEvent';
import {
  VEHICLES_QUERY,
  VEHICLES_QUERY_CONFIG,
  vehiclesQuery,
} from '../../../../../query/vehicles/vehicles';
import { useSinisterStore } from '../../../../../stores/Sinister/sinister.store';
import SinisterSummaryValidation from './SinisterSummary.validation';

const DAMAGE_PARTS = {
  ceilings: 'techos',
  lock: 'cerradura',
  others: 'otros',
  rear_window: 'ventana trasera',
  side_windows: 'ventanas laterales',
  windshield: 'parabrisas',
};

const CRASH_TYPES = {
  damage_in_my_vehicle: 'daño en mi vehículo',
  damage_in_other_vehicle: 'daño en otro vehículo',
  damage_material: 'daño material',
  was_there_injured: 'hubo heridos',
};

const DOCUMENT_TYPES = {
  license: 'Registro de conducir',
  vehicle_documentation: 'Cédula verde o azul',
  police_complaint: 'Denuncia policial',
};

const SinisterSummary = () => {
  const {
    nextStep,
    sinister_body,
    success,
    errors: error,
    updateSinisterBody,
    createSinister,
  } = useSinisterDataContext();
  const { user } = useAuthDataContext();
  const history = useHistory();
  const { logEvent } = useLogEventInAnalytics();

  const user_phone = user.phone.split('-').join('').replace(/^0+/, '');

  const [, dispatch] = useReducer(sinisterReducer, {});

  const [openError, setOpenError] = useState(false);
  const [duplicateComplaint, setDuplicateComplaint] = useState(false);

  const [selectedDate] = React.useState(sinister_body.date);
  const maxDate = Date.now();
  const [defaultValues] = useState({
    email: sinister_body?.driverData?.email || user.email,
    phone_number: sinister_body?.driverData?.phone_number || user_phone,
    vehicle_id: sinister_body?.vehicle_id,
  });

  const { data: vehicles } = useQuery(
    VEHICLES_QUERY,
    vehiclesQuery,
    VEHICLES_QUERY_CONFIG
  );

  const setSinisterType = useSinisterStore((state) => state.setSinisterType);

  const submitSinister = () => {
    let currentDate = new Date(selectedDate);

    let crash_string = '';
    sinister_body?.crash?.additional_information &&
      Object.keys(sinister_body?.crash?.additional_information).forEach(
        (crash_damage) => {
          if (
            sinister_body.crash.additional_information[crash_damage] === true
          ) {
            crash_string =
              crash_string === ''
                ? CRASH_TYPES[crash_damage]
                : crash_string + '|' + CRASH_TYPES[crash_damage];
          }
        }
      );

    let document_string = [];
    sinister_body?.crash?.involved?.documents.forEach((document) => {
      document_string.push(
        document_string.includes(DOCUMENT_TYPES[document.type])
          ? ''
          : DOCUMENT_TYPES[document.type]
      );
    });

    let damage_string = '';

    sinister_body?.damage &&
      Object.keys(sinister_body?.damage).forEach((damage_part) => {
        if (sinister_body?.damage[damage_part] === true) {
          damage_string =
            damage_string === ''
              ? DAMAGE_PARTS[damage_part]
              : damage_string + '|' + DAMAGE_PARTS[damage_part];
        }
      });

    const body: any = {
      type: sinister_body.incident_type,
      need_assistance: sinister_body.need_assistance,
      vehicle_id: sinister_body.vehicle_id,
      date: currentDate,
      address: sinister_body.pickup_location,
      driver: {
        type: sinister_body.policy_owner_was_driving ? 'owner' : 'other',
        fullname: sinister_body.policy_owner_was_driving
          ? user.first_name
            ? user.first_name + ' ' + user.last_name
            : user.last_name
          : sinister_body?.driverData?.full_name,
        identification_number: sinister_body.policy_owner_was_driving
          ? user.identification_number
          : sinister_body?.driverData?.identification_number,
        contact_phone: values.phone_number,
        email: values.email,
      },
      description: sinister_body.fact,
      ambulance_involved: sinister_body?.ambulance_involved,
      public_agent_involved: sinister_body.police_involved,
      damage: {
        crystals: sinister_body?.damage?.side_windows,
        windshield: sinister_body?.damage?.windshield,
        rear_view_window: sinister_body?.damage?.rear_window,
        lock: sinister_body?.damage?.lock,
        roof: sinister_body?.damage?.ceilings,
        others: sinister_body?.damage?.others,
        description: sinister_body?.damageDescription,
      },
      robbery: {
        type: sinister_body?.robbery?.type,
        tire: sinister_body?.robbery?.tire,
        others: sinister_body?.robbery?.others,
        found_car: sinister_body?.robbery?.found_car,
        description: sinister_body?.robbery?.description,
        police_complaint_photos:
          sinister_body?.robbery?.police_complaint_photos,
        tires: sinister_body?.robbery?.tires,
      },
      crash: sinister_body?.crash
        ? {
            ...sinister_body.crash,
            third_party_data: sinister_body.crash.third_party_data
              ? {
                  ...sinister_body.crash.third_party_data,
                  insurance:
                    sinister_body.crash.third_party_data?.insurance
                      ?.description || undefined,
                }
              : null,
            involved: {
              ...sinister_body.crash.involved,
              documents: undefined,
            },
            damaged_parts: sinister_body.crash.damaged_parts || undefined,
          }
        : '',
    };

    const data = {
      event: 'sinister_confirmed',
      data: {
        user_id: user?.id,
        vehicle_brand: sinister_body.vehicle_brand,
        vehicle_model: sinister_body.vehicle_model,
        sinister_type: IncidentsLabels[sinister_body.incident_type],
        driver_type: sinister_body.policy_owner_was_driving
          ? 'Titular'
          : 'tercero',
        damage: sinister_body?.crash?.additional_information && crash_string,
        document:
          sinister_body?.crash &&
          document_string.filter((doc) => doc !== '').join('|'),
        robbery_type: sinister_body?.robbery && sinister_body?.robbery?.type,
        found_car:
          sinister_body?.robbery && sinister_body?.robbery?.found_car
            ? 'Apareció'
            : 'No apareció',
        robbery_part:
          sinister_body?.robbery &&
          `${sinister_body?.robbery?.tires ? 'neumaticos' : ''} ${
            sinister_body?.robbery?.other ? 'Otras Partes' : ''
          }`,
        damge_parts: sinister_body?.damage && damage_string,
        intervention:
          sinister_body?.police_involved || sinister_body?.ambulance_involved
            ? 'si'
            : 'no',
      },
    };

    if (body?.robbery?.type == 'partial') setSinisterType('partial_robbery');
    CustomDataLayerPush(data);
    createSinister(body);
    dispatch({
      type: CLEAN_INJURED_PEOPLE,
    });
  };

  useEffect(() => {
    if (success.sinister) {
      logEvent(CustomEvents.COMPLAINTS_SUCCESS, {
        app: appConfig?.NAME,
      });
      nextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    if (error.sinister && error?.sinister?.errors === 'Incident duplicated') {
      // console.log(error?.sinister?.errors)
      dataLayerPush('siniestro_error', user?.id);
      setDuplicateComplaint(true);
    } else if (error.sinister) {
      // console.log(error?.sinister?.errors)
      dataLayerPush('siniestro_error', user?.id);
      setOpenError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const { values, errors, handleChange } = useForm(
    submitSinister,
    SinisterSummaryValidation,
    defaultValues
  );

  useEffect(() => {
    updateSinisterBody({ vehicle_id: values.vehicle_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.vehicle_id]);

  const { embed } = useAuthDataContext();
  const redirectHome = () => {
    embed
      ? window.parent.postMessage({ return: 'overview' }, '*')
      : history.push('/dashboard');
  };

  const handleFailure = () => {
    setOpenError(false);
    setDuplicateComplaint(false);
    redirectHome();
  };

  return (
    <>
      {/* Error default */}
      <SharedDialog
        open={openError}
        onClose={() => setOpenError(false)}
        content={
          <Box>
            <Box mb={2}>
              <Typography variant="h6" style={{ marginBottom: '15px' }}>
                Ha ocurrido un error.
              </Typography>
              <Typography>
                ¡Lo sentimos! Esta denuncia no puede procesarse. Por favor
                comunicate con nosotros por Whatsapp para cargar tu denuncia.
              </Typography>
            </Box>
          </Box>
        }
        actions={
          <Grid container justify="flex-end">
            <Grid item xs={12} sm={6}>
              <Box m={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleFailure}
                >
                  Entendido
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box m={2} display="flex" alignItems="center">
                <Link
                  href={appConfig?.WHATSAPP_LINK}
                  rel="noopener"
                  target="_blank"
                >
                  <Button variant="outlined" fullWidth color="secondary">
                    <img
                      src={WhatsappSrc}
                      alt="Whatsapp"
                      style={{ marginLeft: '15px' }}
                    />
                    <Typography display="inline">
                      Comunicarme vía Whatsapp
                    </Typography>
                  </Button>
                </Link>
              </Box>
            </Grid>
          </Grid>
        }
      ></SharedDialog>

      {/* Error denuncia duplicada */}
      <SharedDialog
        open={duplicateComplaint}
        onClose={() => setDuplicateComplaint(false)}
        content={
          <Box>
            <Box mb={2}>
              <Typography variant="h6" style={{ marginBottom: '15px' }}>
                Ha ocurrido un error.
              </Typography>
              <Typography>
                Detectamos que ya registraste este siniestro, si necesitás hacer
                una ampliación o modificar datos por favor comunicate con
                nosotros por Whatsapp.
              </Typography>
            </Box>
          </Box>
        }
        actions={
          <Grid container justify="flex-end">
            <Grid item xs={12} sm={6}>
              <Box m={2}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleFailure}
                >
                  Entendido
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box m={2} display="flex" alignItems="center">
                <Link
                  href={appConfig?.WHATSAPP_LINK}
                  rel="noopener"
                  target="_blank"
                >
                  <Button variant="outlined" fullWidth color="secondary">
                    <img
                      src={WhatsappSrc}
                      alt="Whatsapp"
                      style={{ marginLeft: '15px' }}
                    />
                    <Typography display="inline">
                      Comunicarme vía Whatsapp
                    </Typography>
                  </Button>
                </Link>
              </Box>
            </Grid>
          </Grid>
        }
      ></SharedDialog>

      <Grid container>
        <Grid item xs={12}>
          <Box my={3}>
            <Typography variant="h6" color="textPrimary">
              {' '}
              Resumen{' '}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={9} md={8}>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textPrimary">
                {' '}
                Datos del Contacto{' '}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mr={2} my={2}>
                <Typography variant="subtitle2"> Email </Typography>
                <TextField
                  type="email"
                  size="small"
                  onChange={handleChange}
                  fullWidth={true}
                  error={Boolean(errors['email'])}
                  helperText={errors['email']}
                  name="email"
                  id="email"
                  placeholder="Ingresá tu email"
                  variant="outlined"
                  value={values.email}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box mr={2} my={2}>
                <Typography variant="subtitle2"> Teléfono </Typography>
                <TextField
                  InputProps={{
                    inputComponent: NumberFormatCustom as any,
                  }}
                  size="small"
                  onChange={handleChange}
                  fullWidth={true}
                  error={Boolean(errors['phone_number'])}
                  helperText={errors['phone_number']}
                  name="phone_number"
                  id="phone_number"
                  placeholder="Teléfono *"
                  variant="outlined"
                  value={values.phone_number}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} sm={9} md={8}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="textPrimary">
              {' '}
              Datos del vehículo{' '}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box my={2} mr={2}>
              <Typography variant="subtitle2"> Vehículo </Typography>
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                error={!!errors['vehicle_id']}
              >
                <Select
                  value={values?.vehicle_id}
                  onChange={handleChange}
                  id="vehicle_id"
                  name="vehicle_id"
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                >
                  <MenuItem value="" disabled>
                    {' '}
                    Seleccioná el vehículo{' '}
                  </MenuItem>
                  {vehicles?.map((vehicle: any) => (
                    <MenuItem
                      style={{ color: '#000' }}
                      key={vehicle.id}
                      value={vehicle.id}
                    >
                      {vehicle.brand + ' ' + vehicle.model.split(' ')[0]} -
                      Patente {vehicle.license_plate}
                    </MenuItem>
                  ))}
                </Select>
                {errors['vehicle_id'] ? (
                  <FormHelperText>{errors['vehicle_id']}</FormHelperText>
                ) : null}
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* Daños por ... */}
      <Grid item xs={12} sm={9} md={8}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="secondary">
              {sinister_body.incident_type === 'robbery'
                ? sinister_body?.robbery?.type === 'partial'
                  ? 'Robo parcial'
                  : 'Robo total'
                : `Daños por ${IncidentsLabels[sinister_body.incident_type]}`}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box my={2} mr={2}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  style={{ marginBottom: '15px' }}
                >
                  {' '}
                  Lugar del hecho{' '}
                </Typography>
              </Grid>
              <Box style={{ position: 'relative', display: 'inline-block' }}>
                <Grid container>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2"> Localidad* </Typography>
                    <Box mr={2} my={2}>
                      <TextField
                        defaultValue={
                          sinister_body?.pickup_location?.locality || ''
                        }
                        size="small"
                        name="locality"
                        fullWidth={true}
                        id="origin"
                        placeholder="Localidad"
                        variant="outlined"
                        inputProps={{
                          style: { paddingRight: '30px' },
                        }}
                        disabled
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <LocationIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2"> Calle* </Typography>
                    <Box mr={2} my={2}>
                      <TextField
                        defaultValue={
                          sinister_body?.pickup_location?.street || ''
                        }
                        helperText={errors.street || ''}
                        size="small"
                        error={!!errors.street}
                        name="street"
                        fullWidth={true}
                        id="origin"
                        placeholder="Calle*"
                        variant="outlined"
                        inputProps={{
                          style: { paddingRight: '30px' },
                        }}
                        disabled
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <LocationIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">
                      {' '}
                      Número de calle*{' '}
                    </Typography>
                    <Box mr={2} my={2}>
                      <TextField
                        InputProps={{
                          inputComponent: NumberFormatCustom as any,
                          endAdornment: (
                            <InputAdornment position="end">
                              <LocationIcon />
                            </InputAdornment>
                          ),
                        }}
                        defaultValue={
                          sinister_body?.pickup_location?.street_number || ''
                        }
                        size="small"
                        name="street_number"
                        fullWidth={true}
                        id="origin"
                        placeholder="Número de calle*"
                        variant="outlined"
                        disabled
                        inputProps={{
                          style: { paddingRight: '30px' },
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <MuiPickersUtilsProvider locale={deLocale} utils={DateFnsUtils}>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2"> Fecha </Typography>
              <Box pr="30px">
                <KeyboardDatePicker
                  style={{ backgroundColor: 'white', paddingRight: '30px' }}
                  disableToolbar
                  variant="inline"
                  inputVariant="outlined"
                  format="dd/MM/yyyy"
                  margin="normal"
                  id="date-picker"
                  size="small"
                  value={selectedDate}
                  onChange={() => {}}
                  fullWidth={true}
                  maxDate={maxDate}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  keyboardIcon={<CalendarIcon />}
                  disabled
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2"> Hora </Typography>
              <Box pr="30px">
                <KeyboardTimePicker
                  style={{ backgroundColor: 'white', paddingRight: '30px' }}
                  fullWidth={true}
                  onChange={() => {}}
                  inputVariant="outlined"
                  margin="normal"
                  id="time-picker"
                  ampm
                  size="small"
                  value={selectedDate}
                  KeyboardButtonProps={{
                    'aria-label': 'change time',
                  }}
                  keyboardIcon={<TimeIcon />}
                  disabled
                />
              </Box>
            </Grid>
          </Grid>
        </MuiPickersUtilsProvider>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box my={3}>
          <FloatingButton
            onClick={submitSinister}
            disabled={!!Object.keys(errors).length}
          >
            Confirmar denuncia
          </FloatingButton>
        </Box>
      </Grid>
    </>
  );
};

export default SinisterSummary;
